// Ch78 · what the empty Messages pane says before you click anything.
//
// Grant: "what are we doing?" — the pane used to say "Select a conversation".
// Now it answers the question for the current scope: how many replies you
// owe, how much money is out, who to follow up, how big the cold pile is,
// and how many people the Engaged filter is holding back. Every card is a
// button that lands on the view that works it.

import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import type { QueueGroup, QueueRow, QueueViewId } from "./clientQueue";

export interface DaySummaryProps {
  /** needs_action rows (with groups) for the current scope. */
  rows: QueueRow[];
  onPick: (view: QueueViewId, group?: QueueGroup) => void;
}

interface Card {
  key: QueueGroup | "hidden";
  label: string;
  value: string;
  sub: string;
  view: QueueViewId;
  tone: "error" | "warning" | "primary" | "neutral";
}

export function summarize(rows: QueueRow[]): Record<QueueGroup, { count: number; money: number }> {
  const out: Record<QueueGroup, { count: number; money: number }> = {
    reply: { count: 0, money: 0 },
    money: { count: 0, money: 0 },
    followup: { count: 0, money: 0 },
    cold: { count: 0, money: 0 },
  };
  for (const r of rows) {
    if (r.kind !== "row" || !r.group) continue;
    if (r.test) continue; // F24 · a TEST client is never a number on the day
    out[r.group].count += 1;
    if (r.group === "money" && r.invoice) {
      const m = r.invoice.match(/\$([\d,]+)/);
      if (m) out.money.money += Number(m[1].replace(/,/g, ""));
    }
  }
  return out;
}

export default function DaySummary({ rows, onPick }: DaySummaryProps) {
  const theme = useTheme();
  const s = summarize(rows);
  const cards: Card[] = [
    { key: "reply", label: "Replies you owe", value: String(s.reply.count), sub: s.reply.count ? "they texted, nobody answered" : "inbox zero", view: "needs_reply", tone: s.reply.count ? "error" : "neutral" },
    { key: "money", label: "Money out", value: s.money.money ? `$${s.money.money.toLocaleString()}` : "$0", sub: `${s.money.count} open invoice${s.money.count === 1 ? "" : "s"}`, view: "follow_up", tone: s.money.count ? "warning" : "neutral" },
    { key: "followup", label: "Follow up", value: String(s.followup.count), sub: "something live", view: "follow_up", tone: s.followup.count ? "primary" : "neutral" },
    { key: "cold", label: "Gone cold", value: String(s.cold.count), sub: "one check-in text each", view: "follow_up", tone: "neutral" },
  ];
  const color = (tone: Card["tone"]) =>
    tone === "error" ? theme.palette.error.main : tone === "warning" ? theme.palette.warning.main : tone === "primary" ? theme.palette.primary.main : theme.palette.text.secondary;

  return (
    <Box data-testid="messages-day-summary" sx={{ width: "100%", maxWidth: 560 }}>
      <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 0.8 }}>
        Today
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, mt: 1 }}>
        {cards.map((c) => (
          <Box
            key={c.key}
            component="button"
            onClick={() => onPick(c.view, c.key === "hidden" ? undefined : c.key)}
            data-testid={`day-card-${c.key}`}
            sx={{
              textAlign: "left",
              border: `1px solid ${alpha(color(c.tone), 0.4)}`,
              bgcolor: alpha(color(c.tone), 0.06),
              borderRadius: 2,
              p: 1.5,
              cursor: "pointer",
              fontFamily: "inherit",
              color: "text.primary",
              "&:hover": { bgcolor: alpha(color(c.tone), 0.12) },
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{c.label}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, color: color(c.tone) }}>{c.value}</Typography>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>{c.sub}</Typography>
          </Box>
        ))}
      </Box>
      {/* PKT-0693 S2 · the "N never engaged — hidden under Engaged" line lived
          here. Deleted with the mode: nothing is hidden any more, so there is
          no hidden count to report. */}
    </Box>
  );
}
